import { formatAddress } from '@sequencehq/utils'
import {
  Text,
  CustomerLogo,
  Primary13Regular,
  Primary13SemiBold,
  TextOverflow,
  Box
} from '@sequencehq/core-components'
import { getLogoSrc } from 'Customer/utils/logo'
import { CustomerSnapshot } from '../../types'
import {
  ComparisonCard,
  ComparisonVariant,
  getComparisonCardStyle
} from './BeforeAfterComparison'

/**
 * A single customer card (logo, legal name, address) styled for its variant.
 * Used both in the review modal's before/after comparison and, in its `after`
 * form, in the inbox preview so the two read consistently. The caller supplies
 * the `data-testid` so each mount site is addressable independently (the preview
 * card stays mounted behind the modal, so they must not share a test id).
 */
export const CustomerCard = ({
  customer,
  variant,
  highlighted = false,
  'data-testid': dataTestId
}: {
  customer: CustomerSnapshot
  variant: ComparisonVariant
  /** Rings the card in indigo when its form is the one being edited. */
  highlighted?: boolean
  'data-testid'?: string
}) => {
  const { color } = getComparisonCardStyle(variant)
  const addressLines = formatAddress(customer.address)

  return (
    <ComparisonCard
      variant={variant}
      highlighted={highlighted}
      logo={
        <CustomerLogo src={getLogoSrc(customer)} size={32} fallbackSize={16} />
      }
      data-testid={dataTestId}
    >
      {/* Box (not Text) wrapper: TextOverflow renders a div — invalid inside
          Text's <p> — and reads its font from the computed style of its parent,
          so the typography token must live on this wrapper. */}
      <Box {...Primary13SemiBold} color={color} width="100%">
        <TextOverflow>{customer.legalName}</TextOverflow>
      </Box>
      {/* Address lines grouped so they stack tight (spacing driven by
          line-height, not the content column's gap), matching the shared
          `core-components/CustomerCard` — see `packages/core-components`. */}
      {addressLines.length > 0 && (
        <Box data-testid={dataTestId ? `${dataTestId}.address` : undefined}>
          {addressLines.map((line, i) => (
            <Text key={i} {...Primary13Regular} lineHeight="18px" color={color}>
              {line}
            </Text>
          ))}
        </Box>
      )}
    </ComparisonCard>
  )
}
